import { useLocalSearchParams } from "expo-router"
import { useAnalysis, useCompPreferences } from "@/queries/analysis"
import {
  type AnalysisCompInput,
  type AnalysisQuery,
  type AnalysisType,
  type CompQueryPreferenceType,
} from "@/generated/graphql"
import { useMemo } from "react"

export interface UseAnalysisDataReturn {
  isAnalysisLoading: boolean
  defaultsAreLoading: boolean
  analysisQuery: AnalysisQuery | undefined
  analysisId: string
  analysis: AnalysisType | null
  defaultCompPreferences: CompQueryPreferenceType | null
  saveCompPreferences: (compInput: AnalysisCompInput) => void
  defaultsEqualSelected: boolean
}

export const useAnalysisData = (
  passedAnalysisId?: string
): UseAnalysisDataReturn => {
  const { analysisId: paramAnalysisId } = useLocalSearchParams()

  // Use passedAnalysisId if provided, otherwise fallback to paramAnalysisId from search params
  const analysisId =
    passedAnalysisId ||
    (Array.isArray(paramAnalysisId) ? paramAnalysisId[0] : paramAnalysisId)

  // Fetch the main analysis data
  const { data: analysisQuery, isLoading: isAnalysisLoading } =
    useAnalysis(analysisId)

  const analysis = analysisQuery?.analysis || null

  // Fetch default comp preferences
  const { data: defaultCompData, isLoading: defaultsAreLoading } =
    useCompPreferences(analysisId, true)

  const defaultCompPreferences = defaultCompData?.compQueryPreference || null

  const defaultsEqualSelected: boolean = useMemo(() => {
    return (
      JSON.stringify(defaultCompPreferences) ===
      JSON.stringify(analysis?.compQueryPreference)
    )
  }, [analysis, defaultCompPreferences])

  return <UseAnalysisDataReturn>{
    analysisQuery,
    analysis,
    isAnalysisLoading,
    analysisId,
    defaultsAreLoading,
    defaultCompPreferences,
    defaultsEqualSelected,
  }
}
